import React from 'react';

import { translate } from '~l10n/client';

import { xAgoFormatter } from '~shared/components/helpers';
import { IconDocument, IconFolder } from '~shared/components/icons/standard';
import { Search } from '~shared/components/Table/update/components/Search';
import { useCopyDialog } from '~teacher/components/quizzes/CopyMoveDialog/CopyToWorkspaceDialog/CopyToWorkspaceContext';
import {
  DialogQuizItemList,
  commonDateSorter,
} from '~teacher/components/workspaces/DialogQuizItemList/DialogQuizItemList';
import { useWorkspaceItems } from '~teacher/hook/workspaces/useWorkspaceItems';

import { WorkspaceFolder } from '../../../../../api/workspaces/folders/types';

export const SelectFolder = () => {
  const { prevStep, workspaceName, workspaceId, setParentId, parentId } =
    useCopyDialog();
  const [searchQuery, setSearchQuery] = React.useState('');
  // get all items, disable all quizzes from being selected
  const { data, workspaceQueries } = useWorkspaceItems({
    workspaceId,
  });
  const isLoading = workspaceQueries.some((query) => query.isLoading);
  const parentIdFilter = React.useCallback(
    (data) => {
      if (searchQuery) return data;
      return data.filter((item) => {
        if (!workspaceId) {
          return item.parentId === parentId;
        }
        if (item.type === 'quiz') {
          return item.parentFolderId === parentId;
        }
        return item.parentId === parentId;
      });
    },
    [parentId, searchQuery, workspaceId]
  );
  const handleSelection = (item) => {
    if (item.type === 'folder') {
      setSearchQuery('');
      setParentId(item.id);
    } else {
      // will not be called, but used as a safeguard
      return;
    }
  };
  const columns = React.useMemo(
    () => [
      {
        id: 'type',
        disableSortBy: true,
        accessor: (item) => item.type,
        customWidth: '60px',
        Cell: ({ value }) =>
          value === 'quiz' ? <IconDocument /> : <IconFolder />,
      },
      {
        Header: translate('Name'),
        id: 'name',
        accessor: (item) => item.name,
        sortBy: 'alphanumeric',
      },
      {
        Header: translate('Modified'),
        id: 'modified',
        customWidth: '160px',
        sortType: commonDateSorter,
        accessor: (item) => {
          if (!workspaceId) {
            if (item.type === 'quiz') {
              return xAgoFormatter({ date: item.date });
            }
            return xAgoFormatter({ date: item.date });
          } else {
            return item.type === 'quiz'
              ? xAgoFormatter({ date: item.publishedDate })
              : xAgoFormatter({ date: item.createdDate });
          }
        },
      },
    ],
    [workspaceId]
  );
  const handleBackClick = React.useCallback(() => {
    if (parentId === null) {
      // return to workspace menu
      prevStep();
      return;
    }
    const newId = (data as WorkspaceFolder[]).find(
      (item) => item.id === parentId
    ).parentId;
    setSearchQuery('');
    setParentId(newId ? newId : null);
  }, [data, parentId, prevStep, setParentId]);
  return (
    <div className={'w-full h-full flex flex-col'}>
      <Search name={workspaceName} setQuery={setSearchQuery} />
      <DialogQuizItemList
        data={data}
        disableRow={(row) => row.type === 'quiz'}
        workspaceName={workspaceName}
        onBack={handleBackClick}
        parentId={parentId}
        onSelection={handleSelection}
        filters={[parentIdFilter]}
        searchQuery={{ id: 'name', value: searchQuery }}
        isLoading={isLoading}
        columns={columns}
      />
    </div>
  );
};
